import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class DateService {
  private translateService = inject(TranslateService);

  public getDayOfMonthOrdinal(dayIndex: number) {
    return moment(`${dayIndex + 1}-1`, 'D-M').format('Do');
  }

  /**
   * It gets the formatted date of a day with specific index in a leap year.
   * Moment has no default method for generating a leap year, so 2004 is hardcoded as a leap year example.
   */
  public getDayOfLeapYearOrdinal(dayIndex: number) {
    return moment().year(2004).dayOfYear(dayIndex).format('Do MMMM');
  }

  public getDayOfWeek(dayIndex: number): string | undefined {
    const days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

    if (dayIndex < 1 || dayIndex > 7) {
      return undefined;
    }

    const dayKey = days[dayIndex - 1];
    return this.translateService.instant(`WEEKDAY_${dayKey}`);
  }

  getHumanReadableDate(nanosSinceEpoch: number): string {
    return moment(nanosSinceEpoch / 1000000).format('L LT');
  }

  getHumanReadableDateWithSeconds(nanosSinceEpoch: number): string {
    return moment(nanosSinceEpoch / 1000000).format('L LTS');
  }

  getGraylogReadableDateWithoutTimezone(date: Date | number): string {
    if (typeof date === 'number') {
      date = new Date(date / 1000000);
    }
    return moment(date).format('YYYY-MM-DD HH:mm:ss');
  }

  getGridFormattedDate(nanosSinceEpoch: number): string {
    return moment(nanosSinceEpoch / 1000000).format('lll');
  }

  getFiveMinutesAfter(milliseconds: number): number {
    return parseInt(
      moment(milliseconds / Math.pow(10, 6))
        .add(5, 'minutes')
        .format('x'),
      10,
    );
  }

  getFiveMinutesBefore(milliseconds: number): number {
    return parseInt(
      moment(milliseconds / Math.pow(10, 6))
        .subtract(5, 'minutes')
        .format('x'),
      10,
    );
  }

  getTwentyMinutesAgoTime(): number {
    return parseInt(moment(Date.now()).subtract(20, 'minutes').format('X'), 10) * Math.pow(10, 9);
  }
}
